/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action, computed, IObservableArray, toJS, IReactionDisposer} from 'mobx';
import {get, rmFalsyVals, isNumber, switchExpr} from 'mad-utils/lib/shared';

/**************************************** PROJECT MODULES *****************************************/
import {RootStore} from '../root-store';
import {RecordModel} from './record-model';
import {LastRecordStateModel} from './last-record-state-model';

import {areLocalDatesEqual, TabIDs} from '@canimmunize/core-imports';

import {
    Page,
    PageInstance,
    OtherPage,
    PageType,
    SchedulerManager,
    DoseInstance,
    RecordInstance,
} from '@canimmunize/javelinscript';

/********************************************* EXPORT *********************************************/
/**
 *  MobX store for RecordModel pages
 */
export class PagesModel {
    /**
     * Actual record object returned from Javelinscript via jvln.getRecord()
     * (Proxied to recordModel.record for convenience)
     */
    @computed
    public get record(): RecordInstance {
        return this.recordModel.record;
    }

    /**
     * Associated pages -- TODO implement doses on record model
     */
    @observable
    public _pages: IObservableArray<PageInstance> = observable.array([]);

    @computed
    public get pages(): IObservableArray<PageInstance> {
        return this._pages;
    }

    public set pages(pages: IObservableArray<PageInstance>) {
        this._pages.replace((pages && pages.rmFalsyVals()) || []);
    }

    /******************** Pages ********************/
    /**
     * Get all pages of given type, or all if type not given
     * @param {PageType} type Number corresponding to tab type (e.g. Page.Type.ADULT). Empty = ALL
     * @return {first: Int, second: Int} 1st: # of received vaccinations for disease; 2nd: expected
     */
    public getPages = (type?: PageType | string | void) => {
        // Return all if type not given
        if (typeof type === `undefined` || type == null || type === Page.Type.ALL) {
            return SchedulerManager.getPages(
                (this.pages as any) as PageInstance[],
                Page.Type.ALL,
                this.record
            );
        }

        const outType =
            typeof type === `string`
                ? type === OtherPage.Companion.TRAVEL_ID
                    ? Page.Type.TRAVEL
                    : type === OtherPage.Companion.OTHER_ID
                        ? Page.Type.OTHER
                        : 999 // err # = 999
                : type;

        if (outType === 999) throw new Error(`getPages: invalid input type: ${type}`);

        return SchedulerManager.getPages(
            (this.pages as any) as PageInstance[],
            outType,
            this.record
        );
    };

    /**
     * Extract page matching page ID (which can be found on dose.pageId)
     * @return {PageInstance} Page with the given ID
     */
    public getPage = (pageId: string) => this.pages.find(page => page.pageId === pageId);

    /**
     * Get tab type to flip to for displaying given dose
     */
    public getDosePageType = (dose: DoseInstance): TabIDs => {
        /* Quick match */
        // Detect travel & other doses with the dose's pageId
        if (typeof dose.pageId === `string`) {
            if (dose.pageId === OtherPage.Companion.TRAVEL_ID) return `travel`;
            if (dose.pageId === OtherPage.Companion.OTHER_ID) return `other`;
        }

        // Detect child & adult doses using the dose's collection
        if (typeof dose.collection === `function`) {
            const coll = dose.collection();
            const years = get(coll, `max.years`);
            if (
                (isNumber(coll.childIndex) && coll.childIndex > -1) ||
                (isNumber(years) && years < 16)
            ) {
                return `childhood`;
            }
            if (
                (isNumber(coll.adultIndex) && coll.adultIndex > -1) ||
                (isNumber(years) && years >= 16)
            ) {
                return `adult`;
            }
        }

        /* Get page type by searching pages if quick match fails */
        // Find page matching dose
        const matchPage = dose.pageId
            ? this.getPage(dose.pageId)
            : this.getPages().find(
                  page => !!page.doses.find(curDose => curDose.doseId === dose.doseId)
              );

        // Handle no matching page type
        if (!matchPage) {
            const errMsg = `RecordModel#getPageType :: No page type found for dose:`;
            console.error(errMsg, dose);
            throw new Error(`${errMsg} ${dose.doseId}`);
        }

        // Return detected type
        return ([`childhood`, `adult`, `other`, `travel`].find(
            type =>
                matchPage &&
                matchPage.isOnPage(Page.Type[(type === `childhood` ? `child` : type).toUpperCase()])
        ) || `all`) as TabIDs;
    };

    /**
     * Currently selected pages collection (1 pages collection per tab)
     * @return {PageInstance[]} List of generated pages of the requested type
     */
    @computed
    public get currentPages(): PageInstance[] {
        return SchedulerManager.getPages(
            (this.pages as any) as PageInstance[],
            this.currentPageType,
            this.record
        );
    }

    /**
     * Determine if all stored pages should be replaced or not (done if region,
     * birthdate, or gender has changed)
     */
    private shouldReplaceAllPages = () => this.lastRecordStateModel.hasRecordChanged(this.record);

    /**
     * Update outdated Page (PageInstance) objects in pages array (Avoids
     * recreating all Page objects - only updates what's needed)
     *
     * Improves performance, & fixes "2nd-pass rerendering" bug, where when dose
     * save completes, DDVs associated with any page that got recreated get closed
     */
    private updatePagesPartial = (pages: PageInstance[]) => {
        // Make hash from new pages array (performance)
        type NewPagesHash = Record<string, PageInstance>;
        const newPages: NewPagesHash = pages.reduce((acc, pg) => ({...acc, [pg.pageId]: pg}), {});

        /*
         * Replace individual page objects with added or removed doses, or
         * doses with updated date values
         */
        ((this.pages && this.pages.rmFalsyVals()) || []).forEach(
            action((page: PageInstance, pageIdx) => {
                const newPage = newPages[page.pageId];
                if (newPage) {
                    try {
                        if (newPage.doses.length !== page.doses.length) {
                            this.pages[pageIdx] = newPage;
                            return;
                        }
                        for (let doseIdx = 0; doseIdx < page.doses.length; doseIdx++) {
                            const newPageDose = newPage.doses[doseIdx];
                            const pageDose = page.doses[doseIdx];
                            if (
                                !areLocalDatesEqual(
                                    pageDose.overallDate,
                                    newPageDose.overallDate
                                ) ||
                                newPageDose.isReceived !== pageDose.isReceived ||
                                newPageDose.isScheduled !== pageDose.isScheduled ||
                                newPageDose.displayTerm !== pageDose.displayTerm
                            ) {
                                this.pages[pageIdx] = newPage;
                                return;
                            }
                        }
                    } catch (warning) {
                        // Regenerate full page if no date present on a dose
                        this.pages[pageIdx] = newPage;
                    }
                }
            })
        );
    };

    /**
     * If relevant to current record use incoming data to regenerate all changed or
     * currently invalid Page (visit) objects
     */
    private regeneratePages = ({val: recordIdToUpdate}: {val: string}) => {
        // Only update record that received new data
        if (this.record.recordId === recordIdToUpdate) {
            const newPagesArr = SchedulerManager.generatePages(this.record);

            // Replace all pages if record settings changed, otherwise update only changed pages
            if (this.shouldReplaceAllPages()) {
                this.lastRecordStateModel.updateLastRecordSettings(this.record);
                this.pages.replace(rmFalsyVals(newPagesArr));
            } else {
                this.updatePagesPartial(newPagesArr);
            }
        }
    };

    /**
     * Regenerate this record's pages when Javelin's REGENERATE_PAGES action occurs
     */
    private subscribeToRegeneratePagesEvent = action(() => {
        this.recordModel.disposers.push(
            this.rootStore.jvlnsBroadcaster.subscribe(`ev_REGENERATE_PAGES`, this.regeneratePages)
        );
    });

    /**
     * Currently active visit type tab (childhood, adult, travel, other, or all)
     */
    @observable
    public activePageType: TabIDs = `childhood`;

    /**
     * Enum value of currently selected tab
     *
     * Returns integer (from -1 to 3) associated with the current tab type:
     *     -1 = ALL
     *     0 = CHILD
     *     1 = ADULT
     *     2 = TRAVEL
     *     3 = OTHER
     *
     * (Note: 4 and 5 exist as well, but are unused)
     */
    @computed
    public get currentPageType(): PageType {
        // prettier-ignore
        return switchExpr(
            this.activePageType === `childhood`, Page.Type.CHILD,
            this.activePageType === `adult`,     Page.Type.ADULT,
            this.activePageType === `travel`,    Page.Type.TRAVEL,
            this.activePageType === `other`,     Page.Type.OTHER,
                                                 Page.Type.ALL
        );
    }

    @action
    public setCurrentPageType = (currentPageType: TabIDs): void => {
        this.activePageType = currentPageType;
    };

    /**
     * Factory method (true constructor)
     */
    public static new = (
        rootStore: RootStore,
        recordModel: RecordModel,
        lastRecordStateModel: LastRecordStateModel
    ): PagesModel => {
        return new PagesModel(rootStore, recordModel, lastRecordStateModel);
    };

    private constructor(
        protected rootStore: RootStore,
        protected recordModel: RecordModel,
        protected lastRecordStateModel: LastRecordStateModel
    ) {
        const genPages = SchedulerManager.generatePages(this.record);
        this.pages.replace((genPages && rmFalsyVals(genPages)) || []);

        this.subscribeToRegeneratePagesEvent();
    }
}
